<template>
  <div style="height: 100vh; overflow: hidden; display: flex; flex-direction: column;">
    <!-- Top bar -->
    <v-toolbar flat dense class="flex-grow-0" style="border-bottom: 1px solid #ddd;">
      <v-spacer />
      <v-text-field
        v-model="page.businessName"
        placeholder="Page name"
        dense
        hide-details
        outlined
        class="mx-3"
        style="max-width: 300px"
      />
      <div v-if="saving" class="mr-4 grey--text text--darken-1 d-flex align-center">
        <v-progress-circular indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
        <span class="caption">Saving...</span>
      </div>
      <div v-else-if="lastSaved" class="mr-4 grey--text text--darken-1 d-flex align-center">
        <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
        <span class="caption">Saved</span>
      </div>
      <v-btn color="primary" :loading="saving" @click="save('published')">
        <v-icon left small>mdi-publish</v-icon>
        Publish
      </v-btn>
    </v-toolbar>

    <!-- Loading state -->
    <v-container v-if="loading" class="fill-height">
      <v-row align="center" justify="center">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-row>
    </v-container>

    <!-- Elementor-style split layout -->
    <div v-else style="display: flex; flex: 1; overflow: hidden;">
      
      <!-- Editor Sidebar (Left) -->
      <div style="width: 400px; height: 100%; display: flex; flex-direction: column; background: #f9fafb; border-right: 1px solid #ddd;">
        
        <v-tabs v-model="sidebarTab" fixed-tabs background-color="transparent" color="primary" class="flex-grow-0" style="border-bottom: 1px solid #e0e0e0;">
          <v-tab>Blocks</v-tab>
          <v-tab>Theme</v-tab>
          <v-tab>Settings</v-tab>
        </v-tabs>

        <v-tabs-items v-model="sidebarTab" style="flex: 1; overflow-y: auto; background: transparent; height: calc(100vh - 100px);">
          
          <!-- Blocks Tab -->
          <v-tab-item class="pa-4">
            <h3 class="subtitle-1 font-weight-bold mb-3">Page Blocks</h3>
        
        <draggable
          v-model="page.blocks"
          handle=".drag-handle"
          @end="onDragEnd"
        >
          <block-wrapper
            v-for="(block, index) in page.blocks"
            :key="block.id"
            :value="block"
            @input="updateBlock(index, $event)"
            @remove="removeBlock(index)"
            @move-up="moveBlock(index, -1)"
            @move-down="moveBlock(index, 1)"
          />
        </draggable>

        <!-- Add block button -->
        <v-btn block outlined color="primary" class="mt-3 mb-6 bg-white" @click="addDialog = true">
          <v-icon left>mdi-plus</v-icon>
          Add Section
        </v-btn>

        <!-- Page Settings -->
        <p class="text-caption grey--text text--darken-1 mb-2">Legal Terms and Privacy are automatically generated under the Settings tab.</p>
          </v-tab-item>

          <!-- Theme Tab -->
          <v-tab-item class="pa-4">
            <h3 class="subtitle-1 font-weight-bold mb-3">Theme Presets</h3>
            <v-row dense class="mb-4">
              <v-col v-for="(t, idx) in themePresets" :key="idx" cols="6">
                <v-card outlined hover :style="{'border-color': page.colorScheme && page.colorScheme.preset === t.name ? 'var(--v-primary-base)' : ''}" @click="applyTheme(t)">
                  <div :style="{background: t.background, height: '40px', 'border-radius': '4px 4px 0 0' }" class="d-flex align-center justify-center">
                    <div :style="{background: t.primary, width: '20px', height: '20px', borderRadius: t.radius}"></div>
                  </div>
                  <div class="text-caption text-center py-1 font-weight-medium">{{ t.name }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="mb-4"></v-divider>

            <h3 class="subtitle-1 font-weight-bold mb-3">Customize Theme</h3>
            
            <div class="mb-3">
              <span class="caption grey--text text--darken-2 d-block mb-1">Primary Color</span>
              <v-color-picker v-if="page.colorScheme" v-model="page.colorScheme.primary" hide-canvas hide-inputs show-swatches swatches-max-height="100" @input="onThemeChange"></v-color-picker>
            </div>

            <div v-if="page.colorScheme" class="mb-3">
              <span class="caption grey--text text--darken-2 d-block mb-1">Background Color</span>
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-sheet v-bind="attrs" :color="page.colorScheme.background || '#ffffff'" height="30" width="100%" class="rounded" outlined style="cursor: pointer;" v-on="on"></v-sheet>
                </template>
                <v-color-picker v-model="page.colorScheme.background" hide-canvas hide-inputs show-swatches swatches-max-height="100" @input="onThemeChange"></v-color-picker>
              </v-menu>
            </div>

            <div v-if="page.colorScheme" class="mb-3">
              <span class="caption grey--text text--darken-2 d-block mb-1">Text Color</span>
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-sheet v-bind="attrs" :color="page.colorScheme.textColor || '#1f2937'" height="30" width="100%" class="rounded" outlined style="cursor: pointer;" v-on="on"></v-sheet>
                </template>
                <v-color-picker v-model="page.colorScheme.textColor" hide-canvas hide-inputs show-swatches swatches-max-height="100" @input="onThemeChange"></v-color-picker>
              </v-menu>
            </div>

            <div v-if="page.colorScheme" class="mb-3">
              <span class="caption grey--text text--darken-2 d-block mb-1">Secondary Text Color</span>
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-sheet v-bind="attrs" :color="page.colorScheme.textSecondary || '#6b7280'" height="30" width="100%" class="rounded" outlined style="cursor: pointer;" v-on="on"></v-sheet>
                </template>
                <v-color-picker v-model="page.colorScheme.textSecondary" hide-canvas hide-inputs show-swatches swatches-max-height="100" @input="onThemeChange"></v-color-picker>
              </v-menu>
            </div>

            <v-select
              v-if="page.colorScheme"
              v-model="page.colorScheme.font"
              :items="['Inter', 'Roboto', 'Poppins', 'Playfair Display', 'Montserrat']"
              label="Font Family"
              outlined
              dense
              class="mt-4"
              @change="onThemeChange"
            ></v-select>

            <v-select
              v-if="page.colorScheme"
              v-model="page.colorScheme.radius"
              :items="[{text:'Square', value:'0px'}, {text:'Rounded', value:'8px'}, {text:'Pill / Soft', value:'24px'}]"
              label="Corner Style"
              outlined
              dense
              @change="onThemeChange"
            ></v-select>

            <v-switch
              v-if="page.colorScheme"
              v-model="page.colorScheme.isDark"
              label="Dark Mode Sections"
              dense
              @change="onThemeChange"
            ></v-switch>

          </v-tab-item>
  <!-- Settings Tab -->
          <v-tab-item class="pa-4">
              <h3 class="subtitle-1 font-weight-bold mb-3">⚙️ General Settings</h3>

              <ImageUploader
                :value="page.logo || ''"
                label="Logo"
                @input="page.logo = $event; logoSetByUpload = true; queueAutoSave()"
              />
              <div v-if="logoSetByUpload && page.logo" class="d-flex align-center mt-2 mb-3">
                <v-chip small color="success" outlined class="mr-2"><v-icon small left>mdi-check-circle</v-icon>Logo uploaded</v-chip>
                <v-btn x-small text color="red" @click="page.logo = ''; logoSetByUpload = false; queueAutoSave()">Remove</v-btn>
              </div>
              <template v-else>
                <div class="text-center grey--text caption my-2">— or —</div>
                <v-text-field
                  v-model="page.logo"
                  label="Paste Logo URL"
                  placeholder="https://example.com/logo.png"
                  outlined
                  dense
                  class="mb-3"
                  hide-details
                  @input="logoSetByUpload = false; queueAutoSave()"
                ></v-text-field>
              </template>

              <v-select
                v-model="page.logoAlignment"
                :items="[{text:'Left',value:'left'},{text:'Center',value:'center'},{text:'Right',value:'right'}]"
                label="Logo Alignment"
                outlined
                dense
                hide-details
                class="mb-4"
                @change="queueAutoSave"
              ></v-select>

              <v-divider class="my-4"></v-divider>

              <ImageUploader
                :value="page.favicon || ''"
                label="Favicon (32×32 recommended)"
                @input="page.favicon = $event; faviconSetByUpload = true; queueAutoSave()"
              />
              <div v-if="faviconSetByUpload && page.favicon" class="d-flex align-center mt-2 mb-3">
                <v-chip small color="success" outlined class="mr-2"><v-icon small left>mdi-check-circle</v-icon>Favicon uploaded</v-chip>
                <v-btn x-small text color="red" @click="page.favicon = ''; faviconSetByUpload = false; queueAutoSave()">Remove</v-btn>
              </div>
              <template v-else>
                <div class="text-center grey--text caption my-2">— or —</div>
                <v-text-field
                  v-model="page.favicon"
                  label="Paste Favicon URL"
                  placeholder="https://example.com/favicon.png"
                  outlined
                  dense
                  class="mb-3"
                  hide-details
                  @input="faviconSetByUpload = false; queueAutoSave()"
                ></v-text-field>
              </template>

              <div class="caption grey--text mb-2">
                All contacts captured from this page will be saved to the selected list.
              </div>
              <v-select
                v-model="page.listsId"
                :items="lists"
                item-text="name"
                item-value="id"
                label="Linked List"
                placeholder="Select a list for leads"
                outlined
                dense
                hide-details
                class="mb-3"
                @change="queueAutoSave"
              ></v-select>

              <v-divider class="my-4"></v-divider>
              <h3 class="subtitle-1 font-weight-bold mb-3">🔗 URL Settings</h3>
              
              <v-text-field
                v-model="page.slug"
                label="Custom URL Slug"
                placeholder="my-awesome-page"
                outlined
                dense
                class="mb-1"
                hide-details
                @input="queueAutoSave"
              ></v-text-field>
              <div class="caption grey--text mb-5">
                Path: <strong>{{ cleanUrl }}</strong>
              </div>

              <v-divider class="my-4"></v-divider>

            <div class="mb-5 p-3" style="background: #e8f0fe; border-radius: 8px; border: 1px solid #c2d7fb; padding: 16px;">
              <h4 style="color: #1a73e8; margin-bottom: 8px;">Connect Your Domain</h4>
              <p style="font-size: 13px; color: #3c4043; margin-bottom: 12px;">
                Make this landing page appear on your own website address (e.g., <strong>promo.yourdomain.com</strong>).
              </p>
              
              <v-text-field
                v-model="page.customDomain"
                label="Enter Custom Domain"
                placeholder="e.g. promo.yourdomain.com"
                outlined
                dense
                class="mb-2"
                hide-details
              ></v-text-field>
              
              <div v-if="page.customDomain" class="mt-4" style="background: white; padding: 16px; border-radius: 6px; border: 1px dashed #1a73e8; font-size: 13px; color: #3c4043;">
                <h5 style="color: #1a73e8; margin-bottom: 12px;">🛠️ How to Point Your Domain</h5>
                <ol style="margin-bottom: 16px; margin-left: 16px; line-height: 1.5;">
                  <li class="mb-2"><strong>Log in to your Domain Registrar</strong> (e.g., GoDaddy, Namecheap, Cloudflare) and locate the <strong>DNS Management</strong> or <strong>Zone Settings</strong> page.</li>
                  <li class="mb-2"><strong>Delete any conflicting records:</strong> If you already have an <strong>A</strong> or <strong>CNAME</strong> record for <code style="background:#f1f3f4;padding:2px 4px;">{{ page.customDomain.split('.')[0] === 'www' || page.customDomain.split('.').length > 2 ? page.customDomain.split('.')[0] : '@' }}</code>, delete it to prevent routing issues.</li>
                  <li class="mb-2"><strong>Create a new record</strong> using these exact details:</li>
                </ol>
                <div style="background: #f8f9fa; border: 1px solid #dadce0; border-radius: 4px; padding: 12px; margin-bottom: 12px; display: flex; flex-direction: column; gap: 8px;">
                  <div><span style="display:inline-block; width: 100px; color:#5f6368;">Type:</span> <strong>CNAME</strong></div>
                  <div style="display:flex; align-items:flex-start; gap:8px;">
                    <span style="display:inline-block; min-width: 100px; color:#5f6368; padding-top:2px;">Name/Host:</span>
                    <strong style="flex:1; background: #e8eaed; padding: 2px 6px; border-radius: 4px; font-family: monospace; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word;">{{ page.customDomain.split('.')[0] === 'www' || page.customDomain.split('.').length > 2 ? page.customDomain.split('.')[0] : '@' }}</strong>
                    <v-btn x-small text color="primary" @click="handleCopyDnsValue(page.customDomain.split('.')[0] === 'www' || page.customDomain.split('.').length > 2 ? page.customDomain.split('.')[0] : '@')">Copy</v-btn>
                  </div>
                  <div style="display:flex; align-items:flex-start; gap:8px;">
                    <span style="display:inline-block; min-width: 100px; color:#5f6368; padding-top:2px;">Target/Value:</span>
                    <strong style="flex:1; background: #e8eaed; padding: 2px 6px; border-radius: 4px; font-family: monospace; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word;">land.gopages.cc</strong>
                    <v-btn x-small text color="primary" @click="handleCopyDnsValue('land.gopages.cc')">Copy</v-btn>
                  </div>
                </div>
                <div style="color: #5f6368; font-size: 12px; background: #fff8e1; border-left: 3px solid #f4b400; padding: 8px;">
                  <em>Note: DNS propagation can take anywhere from 10 minutes to 24 hours. We will automatically generate a free, secure SSL certificate for your site once the DNS successfully points to us.</em>
                </div>

                <div class="mt-4" style="background: #f0fdf4; padding: 16px; border-radius: 6px; border: 1px dashed #16a34a;">
                  <h5 style="color: #16a34a; margin-bottom: 8px;">&#128274; SSL Certificate Verification (Recommended)</h5>
                  <p style="font-size: 12px; color: #3c4043; margin-bottom: 12px; line-height: 1.5;">
                    For automatic SSL certificate issuance, add this additional DNS record. This enables seamless HTTPS on your custom domain.
                  </p>
                  <div style="background: #f8f9fa; border: 1px solid #dadce0; border-radius: 4px; padding: 12px; display: flex; flex-direction: column; gap: 8px;">
                    <div><span style="display:inline-block; width: 100px; color:#5f6368;">Type:</span> <strong>CNAME</strong></div>
                    <div style="display:flex; align-items:flex-start; gap:8px;">
                      <span style="display:inline-block; min-width: 100px; color:#5f6368; padding-top:2px;">Name/Host:</span>
                      <strong style="flex:1; background: #e8eaed; padding: 2px 6px; border-radius: 4px; font-family: monospace; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word;">_acme-challenge.{{ page.customDomain.split('.')[0] === 'www' || page.customDomain.split('.').length > 2 ? page.customDomain.split('.')[0] : page.customDomain }}</strong>
                      <v-btn x-small text color="primary" @click="handleCopyDnsValue('_acme-challenge.' + (page.customDomain.split('.')[0] === 'www' || page.customDomain.split('.').length > 2 ? page.customDomain.split('.')[0] : page.customDomain))">Copy</v-btn>
                    </div>
                    <div style="display:flex; align-items:flex-start; gap:8px;">
                      <span style="display:inline-block; min-width: 100px; color:#5f6368; padding-top:2px;">Target/Value:</span>
                      <strong style="flex:1; background: #e8eaed; padding: 2px 6px; border-radius: 4px; font-family: monospace; line-height: 1.5; overflow-wrap: anywhere; word-break: break-word;">{{ page.cloudflareDcvTarget || '(save page to generate)' }}</strong>
                      <v-btn x-small text color="primary" :disabled="!page.cloudflareDcvTarget" @click="handleCopyDnsValue(page.cloudflareDcvTarget)">Copy</v-btn>
                    </div>
                  </div>
                  <div style="color: #5f6368; font-size: 11px; margin-top: 8px;">
                    <em>This record delegates SSL certificate validation to our servers. Without it, SSL may take longer to activate or require manual verification.</em>
                  </div>
                </div>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>
            <h3 class="subtitle-1 font-weight-bold mb-3">🔍 SEO Settings</h3>

            <v-text-field
              v-model="page.seoTitle"
              label="SEO Title (Meta Title)"
              placeholder="e.g. Best Call Center Software | Auto Dialer"
              outlined
              dense
              @input="queueAutoSave"
            ></v-text-field>
            
            <v-textarea
              v-model="page.seoDescription"
              label="SEO Description (Meta Description)"
              placeholder="A brief summary of your page to appear in Google search results."
              outlined
              dense
              rows="3"
              @input="queueAutoSave"
            ></v-textarea>

            <div class="mb-2">
              <span style="font-size: 14px; font-weight: 500; color: #1f2937;">SEO Keywords</span><br/>
              <span style="font-size: 12px; color: #6b7280;">Type a keyword and press Enter (e.g. call center, dialer)</span>
            </div>
            <v-combobox
              v-model="seoKeywordsArray"
              chips
              multiple
              deletable-chips
              dense
              outlined
              class="mb-3"
            ></v-combobox>
            <h3 class="subtitle-1 font-weight-bold mb-3">🔗 Campaign URL Builder</h3>
            <p style="font-size: 13px; color: #555; margin-bottom: 12px;">Generate a ready-to-use link with tracking parameters for your ads. Just paste this link into your Facebook or Google Ad manager.</p>
            
            <div class="d-flex mb-3">
              <v-btn small depressed :color="selectedUtmPlatform === 'none' ? 'primary' : ''" class="mr-2" @click="selectedUtmPlatform = 'none'">Direct Link</v-btn>
              <v-btn small depressed :color="selectedUtmPlatform === 'facebook' ? 'primary' : ''" class="mr-2" :style="{ backgroundColor: selectedUtmPlatform === 'facebook' ? '#1877F2' : '', color: selectedUtmPlatform === 'facebook' ? '#fff' : '' }" @click="selectedUtmPlatform = 'facebook'">Facebook Ads</v-btn>
              <v-btn small depressed :color="selectedUtmPlatform === 'google' ? 'primary' : ''" :style="{ backgroundColor: selectedUtmPlatform === 'google' ? '#DB4437' : '', color: selectedUtmPlatform === 'google' ? '#fff' : '' }" @click="selectedUtmPlatform = 'google'">Google Ads</v-btn>
            </div>

            <v-textarea
              :value="generatedUtmUrl"
              readonly
              outlined
              auto-grow
              rows="2"
              class="mb-4"
              style="font-family: monospace; font-size: 13px;"
            >
              <template v-slot:append>
                <div style="display:flex; align-items:flex-start; margin-top: -4px;">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="copyUtmUrl">
                        <v-icon :color="utmCopySuccess ? 'success' : ''">{{ utmCopySuccess ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ utmCopySuccess ? 'Copied!' : 'Copy URL' }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-textarea>

            <v-divider class="my-4"></v-divider>
            <h3 class="subtitle-1 font-weight-bold mb-3">Tracking & Scripts</h3>

            <div v-if="!page.trackingConfig" style="display:none;">
              {{ $set(page, 'trackingConfig', { fbPixelId: '', googleAnalyticsId: '', gtmId: '', customHeadCode: '', customBodyCode: '' }) }}
            </div>

            <template v-if="page.trackingConfig">
              <v-alert type="success" text dense class="mb-4">
                <div class="d-flex align-center">
                  <v-icon color="success" left>mdi-auto-fix</v-icon>
                  <strong>Smart Tracking Active</strong>
                </div>
                <div style="font-size: 11px; margin-top: 4px; line-height:1.4;">Standard events (<strong>PageView</strong> and <strong>Lead</strong> generation) are automatically fired to Facebook and Google below. No extra event code is required.</div>
              </v-alert>

              <div class="mb-4" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px;">
                <div class="d-flex align-center mb-1">
                  <v-icon color="#1877F2" left small>mdi-facebook</v-icon>
                  <strong style="color: #1f2937; font-size:14px;">Facebook Pixel</strong>
                </div>
                <p style="font-size: 11px; color: #666; margin-bottom: 12px; line-height:1.4;">Automatically captures views and leads for Meta ad campaigns.</p>
                <v-text-field
                  v-model="page.trackingConfig.fbPixelId"
                  placeholder="e.g. 1234567890"
                  outlined
                  dense
                  hide-details
                  @input="queueAutoSave"
                ></v-text-field>
              </div>

              <div class="mb-4" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px;">
                <div class="d-flex align-center mb-1">
                  <v-icon color="#DB4437" left small>mdi-google-analytics</v-icon>
                  <strong style="color: #1f2937; font-size:14px;">Google Analytics 4</strong>
                </div>
                <p style="font-size: 11px; color: #666; margin-bottom: 12px; line-height:1.4;">Monitors Google traffic sources and triggers standard lead events.</p>
                <v-text-field
                  v-model="page.trackingConfig.googleAnalyticsId"
                  placeholder="e.g. G-XXXXXXX"
                  outlined
                  dense
                  hide-details
                  @input="queueAutoSave"
                ></v-text-field>
              </div>

              <div class="mb-4" style="background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px;">
                <div class="d-flex align-center mb-1">
                  <v-icon color="#F4B400" left small>mdi-tag-multiple</v-icon>
                  <strong style="color: #1f2937; font-size:14px;">Google Tag Manager</strong>
                </div>
                <p style="font-size: 11px; color: #666; margin-bottom: 12px; line-height:1.4;">Push custom events via dataLayer. Great for advanced triggers.</p>
                <v-text-field
                  v-model="page.trackingConfig.gtmId"
                  placeholder="e.g. GTM-XXXXXXX"
                  outlined
                  dense
                  hide-details
                  @input="queueAutoSave"
                ></v-text-field>
              </div>

              <div class="mb-4" style="background: #f9fafb; border: 1px dashed #d1d5db; border-radius: 8px; padding: 16px;">
                <div class="d-flex align-center mb-1">
                  <v-icon color="#4b5563" left small>mdi-code-tags</v-icon>
                  <strong style="color: #1f2937; font-size:14px;">Custom Scripts Injection</strong>
                </div>
                <p style="font-size: 11px; color: #666; margin-bottom: 12px; line-height:1.4;">For advanced users. Inject TikTok pixels, external chat widgets, scripts, etc.</p>
                
                <v-textarea
                  v-model="page.trackingConfig.customHeadCode"
                  label="Head Code (<head>)"
                  placeholder="<!-- Your custom script here -->"
                  outlined
                  dense
                  rows="3"
                  class="mb-3"
                  hide-details
                  @input="queueAutoSave"
                ></v-textarea>

                <v-textarea
                  v-model="page.trackingConfig.customBodyCode"
                  label="Body Code (<body>)"
                  placeholder="<!-- Your custom script here -->"
                  outlined
                  dense
                  rows="3"
                  hide-details
                  @input="queueAutoSave"
                ></v-textarea>
              </div>
            </template>

          </v-tab-item>

        
        </v-tabs-items>
      </div>

      <!-- Live Preview (Right) -->
      <div style="flex: 1; background: #e5e7eb; padding: 20px; display: flex; justify-content: center; overflow-y: auto;">
        <div style="width: 100%; max-width: 1200px; background: white; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); overflow: hidden; display: flex; flex-direction: column;">
          <!-- Fake Browser Header -->
          <div style="height: 40px; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; padding: 0 12px; gap: 12px;">
            <div style="display: flex; gap: 6px;">
              <div style="width: 12px; height: 12px; border-radius: 50%; background: #ff5f56;"></div>
              <div style="width: 12px; height: 12px; border-radius: 50%; background: #ffbd2e;"></div>
              <div style="width: 12px; height: 12px; border-radius: 50%; background: #27c93f;"></div>
            </div>
            <!-- Address Bar -->
            <div style="flex: 1; background: white; border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 10px; font-size: 13px; color: #64748b; font-family: monospace; display: flex; align-items: center; justify-content: space-between;">
              <span>{{ cleanUrl }}</span>
              <a v-if="page.status === 'published'" :href="cleanUrl" target="_blank" style="text-decoration: none; display: flex; align-items: center;" title="Open in new tab">
                <v-icon small color="#94a3b8">mdi-open-in-new</v-icon>
              </a>
            </div>
          </div>
          <!-- Iframe Preview -->
          <iframe 
            ref="previewFrame" 
            :src="previewUrl" 
            style="width: 100%; flex: 1; border: none;"
          ></iframe>
        </div>
      </div>

    </div>

    <!-- Add block dialog -->
    <v-dialog v-model="addDialog" max-width="500">
      <v-card>
        <v-card-title>Add Section</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col
              v-for="bt in blockTypes"
              :key="bt.type"
              cols="6"
              sm="4"
            >
              <v-card
                outlined
                hover
                class="pa-3 text-center"
                style="cursor: pointer"
                @click="addBlock(bt.type)"
              >
                <v-icon large color="primary">{{ bt.icon }}</v-icon>
                <div class="caption mt-1">{{ bt.label }}</div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="addDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import draggable from "vuedraggable";
import BlockWrapper from "@/components/gopagesv2/BlockWrapper.vue";
import LegalConfigPanel from "@/components/gopagesv2/LegalConfigPanel.vue";
import ImageUploader from "@/components/gopagesv2/editors/ImageUploader.vue";
import fb from "../js/firebase.js";

var BLOCK_TYPES = [
  { type: "hero", label: "Hero", icon: "mdi-image-text" },
  { type: "trust-icons", label: "Trust Logos", icon: "mdi-shield-check" },
  { type: "features", label: "Features", icon: "mdi-view-grid" },
  { type: "two-column-features", label: "2-Col Feature", icon: "mdi-view-split-vertical" },
  { type: "testimonials", label: "Testimonials", icon: "mdi-format-quote-close" },
  { type: "faq", label: "FAQ", icon: "mdi-help-circle" },
  { type: "content-left", label: "Content Left", icon: "mdi-page-layout-sidebar-left" },
  { type: "content-right", label: "Content Right", icon: "mdi-page-layout-sidebar-right" },
  { type: "cta", label: "Call to Action", icon: "mdi-bullhorn" },
  { type: "form", label: "Lead Form", icon: "mdi-card-text-outline" },
  { type: "footer", label: "Footer", icon: "mdi-page-layout-footer" },
];

var DEFAULT_CONTENT = {
  hero: { headline: "", subheadline: "", ctaText: "", variant: "centered" },
  "trust-icons": { title: "Trusted By", logos: ["", "", ""] },
  features: { title: "", subtitle: "", items: [{icon:"mdi-star", title:"", description:""}] },
  "two-column-features": { 
    title: "Why Choose Us?", 
    subtitle: "", 
    leftImage: "", 
    rightImage: "", 
    leftPoints: ["Fast performance", "Secure data"], 
    rightPoints: ["24/7 Support", "Easy to use"] 
  },
  testimonials: { title: "", items: [{quote:"", author:"", role:""}] },
  faq: { title: "", items: [{question:"", answer:""}] },
  "content-left": { title: "", text: "", image: "", ctaText: "" },
  "content-right": { title: "", text: "", image: "", ctaText: "" },
  cta: { headline: "", subtext: "", ctaText: "" },
  form: { title: "", subtitle: "", fields: ["name", "email", "phone"], buttonText: "Submit" },
  footer: { text: "", links: [] },
};

export default {
  name: "GoPagesV2Editor",
  components: { draggable, BlockWrapper, LegalConfigPanel, ImageUploader },
  data() {
    return {
      sidebarTab: 0,
      selectedUtmPlatform: 'none',
      utmCopySuccess: false,
      themePresets: [
        { name: 'Minimal', primary: '#18181b', secondary: '#09090b', background: '#ffffff', textColor: '#27272a', textSecondary: '#71717a', font: 'Inter', radius: '0px', isDark: false },
        { name: 'Corporate', primary: '#2563eb', secondary: '#1d4ed8', background: '#ffffff', textColor: '#1e293b', textSecondary: '#64748b', font: 'Roboto', radius: '4px', isDark: false },
        { name: 'Modern AI', primary: '#6d28d9', secondary: '#4c1d95', background: '#0f172a', textColor: '#f8fafc', textSecondary: '#94a3b8', font: 'Inter', radius: '12px', isDark: true },
        { name: 'Startup Pop', primary: '#f43f5e', secondary: '#e11d48', background: '#fff1f2', textColor: '#334155', textSecondary: '#64748b', font: 'Poppins', radius: '24px', isDark: false }
      ],
      loading: true,
      saving: false,
      lastSaved: false,
      faviconSetByUpload: false,
      logoSetByUpload: false,
      lists: [],
      regenerating: false,
      addDialog: false,
      blockTypes: BLOCK_TYPES,
      page: {
        businessName: "",
        slug: "",
        blocks: [],
        globalStyles: {},
        colorScheme: { primary: '#2563eb', secondary: '#1E40AF', background: '#ffffff', textColor: '#1f2937', textSecondary: '#6b7280', font: 'Inter', radius: '8px', isDark: false, preset: '' },
        status: "draft",
      },
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "teams", "selectedTeam"]),
    seoKeywordsArray: {
      get() {
        return this.page.seoKeywords ? this.page.seoKeywords.split(',').filter(k => k.trim()) : [];
      },
      set(val) {
        this.page.seoKeywords = Array.isArray(val) ? val.join(',') : val;
        this.queueAutoSave();
      }
    },
    generatedUtmUrl() {
      let base = this.page.customDomain ? `https://${this.page.customDomain}` : this.cleanUrl;
      if (!base) return "";
      
      if (this.selectedUtmPlatform === 'facebook') {
        return `${base}?utm_source=facebook&utm_medium=paid_social&utm_campaign={{campaign.name}}&utm_content={{ad.name}}&utm_term={{adset.name}}`;
      } else if (this.selectedUtmPlatform === 'google') {
        return `${base}?utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={creative}&utm_term={keyword}`;
      }
      return base;
    },
    pageId() {
      return this.$route.params.id;
    },
    gopagePreviewBaseUrl() {
      if (!this.ENDPOINT) return window.location.origin;
      return this.ENDPOINT
        .replace(/\/meta\/api$/, "")
        .replace(/\/api$/, "");
    },
    cleanUrl() {
      if (!this.page.slug) return "";
      if (process.env.VUE_APP_GOPAGES_DOMAIN) {
        return `${process.env.VUE_APP_GOPAGES_DOMAIN}/${this.page.slug}`;
      }
      var baseUrl = this.gopagePreviewBaseUrl;
      return baseUrl + "/p/" + this.page.slug;
    },
    previewUrl() {
      if (!this.page.slug) return "";
      var token = this.$store.getters.user ? this.$store.getters.user.token : "";
      if (process.env.VUE_APP_GOPAGES_DOMAIN) {
        return `${process.env.VUE_APP_GOPAGES_DOMAIN}/${this.page.slug}?preview=1&access_token=${token}`;
      }
      var baseUrl = this.gopagePreviewBaseUrl;
      return baseUrl + "/p/" + this.page.slug + "?preview=1&access_token=" + token;
    }
  },
  created() {
    this.loadPage();
    this.fetchLists();
  },
  mounted() {
    window.addEventListener("message", this.handleIframeMessage);
    fb.log("gopage_editor_opened", { pageId: this.pageId });
  },
  beforeDestroy() {
    window.removeEventListener("message", this.handleIframeMessage);
  },
  methods: {
    copyUtmUrl() {
      navigator.clipboard.writeText(this.generatedUtmUrl).then(() => {
        this.utmCopySuccess = true;
        setTimeout(() => { this.utmCopySuccess = false; }, 2000);
      });
    },
    copyToClipboard(text) {
      if (!text) return Promise.reject(new Error("No text to copy"));
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }
      return new Promise((resolve, reject) => {
        try {
          var textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.opacity = "0";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          var successful = document.execCommand("copy");
          document.body.removeChild(textArea);
          if (successful) resolve();
          else reject(new Error("Copy command failed"));
        } catch (e) {
          reject(e);
        }
      });
    },
    handleCopyDnsValue(value) {
      this.copyToClipboard(value)
        .then(() => this.$swal("Copied", "DNS value copied to clipboard", "success"))
        .catch(() => this.$swal("Error", "Unable to copy DNS value", "error"));
    },
    applyTheme(preset) {
      if (!this.page.colorScheme) this.$set(this.page, 'colorScheme', {});
      this.page.colorScheme.preset = preset.name;
      this.page.colorScheme.primary = preset.primary;
      this.page.colorScheme.secondary = preset.secondary || preset.primary;
      this.page.colorScheme.background = preset.background;
      this.page.colorScheme.textColor = preset.textColor;
      this.page.colorScheme.textSecondary = preset.textSecondary || preset.textColor;
      this.page.colorScheme.font = preset.font;
      this.page.colorScheme.radius = preset.radius;
      this.page.colorScheme.isDark = preset.isDark;
      this.queueAutoSave();
      this.pushThemeLive();
    },
    pushThemeLive() {
      if (this.$refs.previewFrame && this.$refs.previewFrame.contentWindow && this.page.colorScheme) {
        this.$refs.previewFrame.contentWindow.postMessage({
          type: "GOPAGES_UPDATE_THEME",
          theme: JSON.parse(JSON.stringify(this.page.colorScheme))
        }, "*");
      }
    },
    onThemeChange() {
      this.page.colorScheme.preset = '';
      this.pushThemeLive();
      this.queueAutoSave();
    },
    handleIframeMessage(e) {
      if (e.data && e.data.type === "GOPAGES_EDIT_BLOCK") {
        var blockId = e.data.blockId ? e.data.blockId.replace('block-', '') : '';
        // Find the block wrapper index by id
        var index = this.page.blocks.findIndex((b) => b.id === blockId); console.log("Index", index, blockId);
        if (index !== -1) {
          // You could trigger an event to expand it
          console.log("Emit!", blockId); this.$root.$emit("GOPAGES_EXPAND_BLOCK", blockId);
          // Scroll left panel to the block (optional, we could let the component handle it)
        }
      }
    },
    // Add a debounced save to trigger visual update in iframe
    queueAutoSave() {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => {
        this.saving = true;
        // Keep the current status so we don't accidentally unpublish on auto-save
        this.save(this.page.status || 'draft', true);
      }, 3000);
    },
    loadPage() {
      this.loading = true;
      this.$http
        .get(this.ENDPOINT + "/gopages/" + this.pageId)
        .then(function (res) {
          var data = res.body;
          if (!data.blocks || !Array.isArray(data.blocks)) {
            data.blocks = [];
          }
          if (!data.colorScheme) {
            data.colorScheme = { primary: '#2563eb', secondary: '#1E40AF', background: '#ffffff', textColor: '#1f2937', textSecondary: '#6b7280', font: 'Inter', radius: '8px', isDark: false, preset: '' };
          }
          this.page = data;
          if (data.favicon) this.faviconSetByUpload = true;
          if (data.logo) this.logoSetByUpload = true;
          if (!data.logoAlignment) this.$set(this.page, 'logoAlignment', 'left');
        }.bind(this))
        .catch(function () {
          this.$swal("Error", "Failed to load page", "error");
          this.$router.push({ name: "GoPagesV2" });
        }.bind(this))
        .finally(function () {
          this.loading = false;
        }.bind(this));
    },
    async fetchLists() {
      try {
        var teamIds = [];
        if (this.teams && this.teams.length) {
          teamIds = this.teams.map(function(t) { return t.id; }).filter(function(id) { return id !== 'allTeams'; });
        }
        var response = await this.$http.post(this.ENDPOINT + "/lists/fetch", { teams: teamIds });
        this.lists = (response.body || []).filter(function(l) { return l.id !== 'allLists'; });
      } catch (e) {
        console.error("Failed to fetch lists", e);
      }
    },
    updateBlock(index, updated) {
      this.$set(this.page.blocks, index, updated);
      this.queueAutoSave();
    },
    removeBlock(index) {
      if (this.page.blocks[index].type === 'form') {
        this.$swal('Cannot Remove', 'The form block cannot be removed because it is required for lead capture.', 'warning');
        return;
      }
      this.page.blocks.splice(index, 1);
      this.queueAutoSave();
    },
    moveBlock(index, direction) {
      if (index + direction < 0 || index + direction >= this.page.blocks.length) return;
      var block = this.page.blocks.splice(index, 1)[0];
      this.page.blocks.splice(index + direction, 0, block);
      this.onDragEnd();
    },
    addBlock(type) {
      this.page.blocks.push({
        id: "block_" + Date.now(),
        type: type,
        order: this.page.blocks.length,
        content: JSON.parse(JSON.stringify(DEFAULT_CONTENT[type] || {})),
        styles: {},
      });
      this.addDialog = false;
      this.queueAutoSave();
    },
    onDragEnd() {
      for (var i = 0; i < this.page.blocks.length; i++) {
        this.page.blocks[i].order = i;
      }
      this.queueAutoSave();
    },
    regeneratePage() {
      this.$swal({
        title: "Regenerate Content?",
        text: "This will overwrite your existing blocks with new AI-generated layout and copy. Proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, regenerate",
      }).then((result) => {
        if (result.isConfirmed) {
          this.regenerating = true;
          fb.log("gopage_ai_regenerate", { pageId: this.pageId });
          this.$http.post(this.ENDPOINT + "/gopages/" + this.pageId + "/generateContent")
            .then((res) => {
              this.page = res.body;
              this.refreshIframe();
              this.$swal("Success", "Page regenerated!", "success");
            })
            .catch((err) => {
              var msg = (err.body && err.body.error && err.body.error.message) || "Regeneration failed";
              this.$swal("Error", msg, "error");
            })
            .finally(() => {
              this.regenerating = false;
            });
        }
      });
    },
    refreshIframe() {
      var iframe = this.$refs.previewFrame;
      if (iframe) {
        // Workaround for ESLint no-self-assign to force iframe reload
        iframe.src = iframe.src + "";
      }
    },
    save(statusStr, isAutoSave = false) {
      if (statusStr === "published") {
        var blocks = this.page.blocks || [];
        for (var i = 0; i < blocks.length; i++) {
          var b = blocks[i];
          if (b.type === "features") {
            if (!b.content.items || b.content.items.length === 0) {
              this.$swal("Validation Error", "Feature blocks must have at least one item.", "error");
              return;
            }
            if (b.content.items.some(item => !item.title || item.title.trim() === "")) {
              this.$swal("Validation Error", "All feature items must have a title before publishing.", "error");
              return;
            }
          } else if (b.type === "testimonials") {
            if (b.content.items && b.content.items.some(item => !item.author || item.author.trim() === "")) {
              this.$swal("Validation Error", "All testimonials must have an author before publishing.", "error");
              return;
            }
          } else if (b.type === "faq") {
            if (b.content.items && b.content.items.some(item => {
              var q = item.question || item.q || "";
              return q.trim() === "";
            })) {
              this.$swal("Validation Error", "All FAQs must have a question before publishing.", "error");
              return;
            }
          }
        }
      }

      this.saving = true;
      var payload = Object.assign({}, this.page, { status: statusStr });
      this.$http
        .put(this.ENDPOINT + "/gopages/" + this.pageId, payload)
        .then(function (res) {
          this.page = res.body;
          if (isAutoSave) {
            this.refreshIframe();
          } else {
            if (statusStr === "published") {
              fb.log("gopage_published", { pageId: this.pageId, slug: this.page.slug, customDomain: this.page.customDomain || null });
              var publishedUrl = this.cleanUrl;
              this.$swal({
                title: "Page Published!",
                icon: "success",
                html:
                  '<p style="color:#64748b;font-size:14px;margin-bottom:10px;">Your landing page is now live. Share the link below with your audience.</p>' +
                  '<div style="display:flex;align-items:center;gap:8px;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;">' +
                    '<span style="flex:1;font-family:monospace;font-size:13px;color:#334155;word-break:break-all;text-align:left;">' + publishedUrl + '</span>' +
                    '<button id="swal-copy-btn" type="button" style="flex-shrink:0;background:#4f46e5;color:white;border:none;border-radius:4px;padding:6px 12px;font-size:12px;cursor:pointer;display:flex;align-items:center;gap:4px;">' +
                      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>' +
                      'Copy' +
                    '</button>' +
                  '</div>',
                onOpen: function () {
                  var btn = document.getElementById("swal-copy-btn");
                  if (btn) {
                    btn.addEventListener("click", function () {
                      var ta = document.createElement("textarea");
                      ta.value = publishedUrl;
                      ta.style.position = "fixed";
                      ta.style.left = "-9999px";
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand("copy");
                      document.body.removeChild(ta);
                      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
                      btn.style.background = "#16a34a";
                      setTimeout(function () {
                        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy';
                        btn.style.background = "#4f46e5";
                      }, 2000);
                    });
                  }
                }
              });
            } else {
              this.$swal("Success", "Draft saved!", "success");
            }
            this.refreshIframe();
          }
        }.bind(this))
        .catch(function (err) {
          if (!isAutoSave) {
            var msg = (err.body && err.body.error && err.body.error.message) || "Something went wrong";
            this.$swal("Error", msg, "error");
          }
        }.bind(this))
        .finally(function () {
          this.saving = false;
          this.lastSaved = true;
        }.bind(this));
    },
  },
};
</script>
